import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Repeat2, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";

import { loginInputSchema, type LoginInput, type SessionResponse } from "@learn-flow/contracts";

import { Logo } from "../../components/logo.js";
import { ApiError, apiRequest } from "../../lib/api.js";
import { sessionQueryKey, sessionQueryOptions } from "./session.js";
import { ResendVerificationForm } from "./verify-email-page.js";
import { GoogleSignIn } from "./google-sign-in.js";

export function LoginPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const sessionQuery = useQuery(sessionQueryOptions);
  const passwordInput = useRef<HTMLInputElement | null>(null);
  const [passwordFocusRequest, setPasswordFocusRequest] = useState(0);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const {
    register,
    handleSubmit,
    getValues,
    setError,
    setValue,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginInputSchema),
    defaultValues: { email: "", password: "" },
  });
  const passwordRegistration = register("password");

  const login = useMutation({
    mutationFn: (input: LoginInput) =>
      apiRequest<SessionResponse>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (session) => {
      setVerificationRequired(false);
      queryClient.setQueryData(sessionQueryKey, session);
    },
    onError: (error: Error) => {
      const requiresVerification =
        error instanceof ApiError && error.problem?.code === "EMAIL_VERIFICATION_REQUIRED";
      setVerificationRequired(requiresVerification);
      const message =
        error instanceof ApiError && error.status === 429
          ? "Too many login attempts. Try again shortly."
          : requiresVerification
            ? "Verify your email before logging in."
            : error instanceof ApiError && error.status === 401
              ? "Email or password is incorrect."
              : "We couldn’t log you in. Please try again.";
      setError("root", { message });
      setValue("password", "");
      setPasswordFocusRequest((request) => request + 1);
    },
  });

  useEffect(() => {
    document.title = "Log in · MemoKite";
  }, []);

  useEffect(() => {
    if (passwordFocusRequest === 0 || login.isPending) return;
    passwordInput.current?.focus();
  }, [login.isPending, passwordFocusRequest]);

  if (sessionQuery.isLoading) {
    return <FullPageStatus message="Opening your learning space…" />;
  }
  if (sessionQuery.data) return <Navigate to="/today" replace />;

  const submit = handleSubmit((input) => login.mutate(input));

  return (
    <main className="auth-shell">
      <section className="auth-panel" aria-labelledby="login-heading">
        <Logo />

        <div className="auth-form-wrap">
          <span className="section-kicker">Welcome back</span>
          <h1 id="login-heading">Continue your learning flow</h1>
          <p>Your library, learning plan, and upcoming sessions are waiting for you.</p>

          <form className="auth-email-form" onSubmit={submit} noValidate>
            <label htmlFor="email">
              Email
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={errors.email ? "true" : undefined}
                disabled={login.isPending}
                {...register("email")}
              />
            </label>
            {errors.email?.message && <p className="field-error">{errors.email.message}</p>}

            <label htmlFor="password">
              Password
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password || errors.root ? "true" : undefined}
                disabled={login.isPending}
                {...passwordRegistration}
                ref={(element) => {
                  passwordRegistration.ref(element);
                  passwordInput.current = element;
                }}
              />
            </label>
            {errors.password?.message && <p className="field-error">{errors.password.message}</p>}

            <div className="auth-message" aria-live="assertive" aria-atomic="true">
              {errors.root?.message && (
                <p className="login-error" role="alert">
                  {errors.root.message}
                </p>
              )}
            </div>

            <button className="primary-button" type="submit" disabled={login.isPending}>
              {login.isPending ? (
                <>
                  <span className="spinner" aria-hidden="true" />
                  Logging in…
                </>
              ) : (
                "Log in"
              )}
            </button>
          </form>
          <p id="login-google-disclosure" className="auth-signup-disclosure">
            By continuing with Google, you confirm that you are at least 16 and agree to the{" "}
            <Link to="/terms">Terms of Service</Link> and acknowledge the{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <GoogleSignIn disclosureId="login-google-disclosure" />
          {verificationRequired ? (
            <ResendVerificationForm initialEmail={getValues("email")} />
          ) : null}
          <p className="auth-switch">
            Need an account? <Link to="/">Sign up</Link>
          </p>
        </div>

        <div className="auth-trust">
          <ShieldCheck size={15} aria-hidden="true" />
          Your learning stays private to you.
        </div>
      </section>

      <aside className="auth-visual" aria-hidden="true">
        <div className="auth-orbit auth-orbit-one" />
        <div className="auth-orbit auth-orbit-two" />
        <div className="auth-visual-copy">
          <span>Collect · Plan · Revisit</span>
          <blockquote>Make time for what matters.</blockquote>
        </div>
        <div className="auth-book">
          <span />
          <span />
        </div>
        <div className="auth-mini-card auth-mini-card-one">
          <i className="lavender-dot" />
          <span>Read with intention</span>
          <Check size={14} />
        </div>
        <div className="auth-mini-card auth-mini-card-two">
          <i className="sage-dot" />
          <span>Session in 3 days</span>
          <Repeat2 size={14} />
        </div>
      </aside>
    </main>
  );
}

export function FullPageStatus({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}): React.JSX.Element {
  return (
    <main className="full-page-status" role="status">
      <Logo />
      <span className="spinner" aria-hidden="true" />
      <p>{message}</p>
      {actionLabel && onAction ? (
        <button type="button" className="soft-button" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
    </main>
  );
}
